import React from "react"
import { Link } from "gatsby"
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiClock,
  FiArrowRight,
  FiExternalLink,
  FiHelpCircle,
  FiActivity,
} from "react-icons/fi"
import { FaLinkedinIn, FaInstagram, FaFacebookF } from "react-icons/fa"
import { AiOutlineYoutube } from "react-icons/ai"

import Layout from "../components/Layout"
import Header from "../components/Header"
import IND from "../images/icons/new/IND.svg"
import UAE from "../images/icons/new/UAE.svg"

const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/premagic/",
    icon: <FaLinkedinIn />,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/premagicdotcom/",
    icon: <FaInstagram />,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/premagicdotcom/",
    icon: <FaFacebookF size={16} />,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCyhi-GvR0rrBq639Z8uWtqw/videos",
    icon: <AiOutlineYoutube size={20} />,
  },
]

const OFFICES = [
  {
    flag: UAE,
    city: "Dubai, UAE",
    company: "Premagic CO LLC",
    address: ["Office B2007-235, Latifa Tower", "Sheikh Zayed Road, Dubai", "United Arab Emirates"],
    email: "sales@premagic.com",
    phones: ["+971 58 533 7576", "+971 52 945 3727"],
  },
  {
    flag: IND,
    city: "Kochi, India",
    company: "Wellbeing Studios Private LTD",
    address: ["IX/483, Grand Central By Group,", "Opp. Infopark Road, Edachira P O,", "Kakkanad, Kochi, Kerala"],
    email: "support@premagic.com",
    phones: ["+91 7829 419 174"],
  },
]

function ContactCard({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-gray-200 p-7 bg-white hover:shadow-lg transition-shadow flex flex-col">
      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EDE4FD] text-[#7253F5] text-xl mb-5">
        {icon}
      </span>
      <h2 className="text-xl font-bold text-[#121212] mb-1">{title}</h2>
      <p className="!text-sm text-[#636363] mb-5">{description}</p>
      <div className="mt-auto space-y-2.5">{children}</div>
    </div>
  )
}

export default function ContactUs() {
  const isBrowser = () => typeof window !== "undefined"
  const isUAEDomain = isBrowser() && window.location.host.includes("ae")

  const salesEmail = isUAEDomain ? "sales@premagic.ae" : "sales@premagic.com"
  const supportEmail = isUAEDomain ? "support@premagic.ae" : "support@premagic.com"
  const salesPhone = isUAEDomain ? "+971 52 945 3727" : "+91 9895096532"

  return (
    <Layout
      title="Contact Premagic - AI Event Platform Support"
      description="Get expert support for your AI-powered events. Contact our team for demos, pricing & custom event technology solutions."
      keywords={[
        "contact Premagic, AI event platform support, event technology consultation, event platform demo, AI photo sharing contact, event management support, enterprise event solutions, event tech customer service, conference technology support, corporate event platform, event AI consultation, photo distribution support, event networking platform, MICE technology support, event platform pricing",
      ]}
      breadcrumbs={[
        { name: "Home", url: "/" },
        { name: "Contact Us", url: "/contact-us/" },
      ]}
    >
      <Header />

      <section className="pm-container py-12">
        {/* Hero */}
        <div className="max-w-2xl py-10">
          <h1 className="font-light text-4xl md:text-5xl mb-5">
            Talk to <span className="font-bold">Us</span>
          </h1>
          <p className="text-[#636363]">
            Questions about pricing, a live demo, or help with your event?
            We&apos;d love to hear from you — pick whichever way is easiest below.
          </p>
        </div>

        {/* Demo CTA */}
        <div className="rounded-2xl px-6 py-5 bg-[#7251F4] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-white font-semibold !text-base">
            Want to see Premagic in action?
          </p>
          <Link
            to="/request-a-demo/"
            className="shrink-0 inline-flex items-center gap-2 bg-white text-[#7251F4] font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-white/90 transition-colors"
          >
            Book a demo
            <FiArrowRight />
          </Link>
        </div>

        {/* Ways to reach us */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          <ContactCard
            icon={<FiMail />}
            title="Talk to sales"
            description="Demos, pricing, and partnership enquiries."
          >
            <a
              href={`mailto:${salesEmail}?Subject=Enquiry`}
              className="flex items-center gap-2 !text-sm font-semibold text-[#121212] hover:text-[#7253F5] transition-colors"
            >
              <FiMail className="shrink-0 text-[#7253F5]" />
              {salesEmail}
            </a>
            <a
              href={`tel:${salesPhone.replace(/\s/g, "")}`}
              className="flex items-center gap-2 !text-sm font-semibold text-[#121212] hover:text-[#7253F5] transition-colors"
            >
              <FiPhone className="shrink-0 text-[#7253F5]" />
              {salesPhone}
            </a>
          </ContactCard>

          <ContactCard
            icon={<FiHelpCircle />}
            title="Get support"
            description="Already using Premagic and need a hand?"
          >
            <a
              href={`mailto:${supportEmail}?Subject=Help`}
              className="flex items-center gap-2 !text-sm font-semibold text-[#121212] hover:text-[#7253F5] transition-colors"
            >
              <FiMail className="shrink-0 text-[#7253F5]" />
              {supportEmail}
            </a>
            <a
              href="https://support.premagic.com/hc/home"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 !text-sm font-semibold text-[#121212] hover:text-[#7253F5] transition-colors"
            >
              <FiExternalLink className="shrink-0 text-[#7253F5]" />
              Visit the Help Center
            </a>
            <a
              href="https://status.premagic.com/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 !text-sm font-semibold text-[#121212] hover:text-[#7253F5] transition-colors"
            >
              <FiActivity className="shrink-0 text-[#7253F5]" />
              System status
            </a>
          </ContactCard>

          <ContactCard
            icon={<FiClock />}
            title="Business hours"
            description="When you can expect a reply from our team."
          >
            <p className="!text-sm text-[#636363]">Monday – Friday</p>
            <p className="!text-sm font-semibold text-[#121212]">
              9:00 AM – 5:00 PM {isUAEDomain ? "GST" : "IST"}
            </p>
            <p className="!text-sm font-semibold text-[#121212]">
              8:00 AM – 4:00 PM PST
            </p>
          </ContactCard>
        </div>

        {/* Offices */}
        <h2 className="font-light text-3xl md:text-4xl mt-20 mb-8">
          Our <span className="font-bold">Offices</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {OFFICES.map(office => (
            <div
              key={office.city}
              className="rounded-2xl border border-gray-200 p-7 bg-white"
            >
              <div className="flex items-center gap-3 mb-4">
                <img src={office.flag} alt={office.city} width={28} height={28} />
                <h3 className="text-lg font-bold text-[#121212]">{office.city}</h3>
              </div>
              <p className="flex gap-2 !text-sm text-[#636363] leading-6">
                <FiMapPin className="shrink-0 mt-1 text-[#7253F5]" />
                <span>
                  <span className="font-semibold text-[#121212]">
                    {office.company}
                  </span>
                  <br />
                  {office.address.map(line => (
                    <React.Fragment key={line}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </span>
              </p>
              <div className="mt-4 space-y-2">
                <a
                  href={`mailto:${office.email}`}
                  className="flex items-center gap-2 !text-sm font-semibold text-[#121212] hover:text-[#7253F5] transition-colors"
                >
                  <FiMail className="shrink-0 text-[#7253F5]" />
                  {office.email}
                </a>
                {office.phones.map(phone => (
                  <a
                    key={phone}
                    href={`tel:${phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-2 !text-sm font-semibold text-[#121212] hover:text-[#7253F5] transition-colors"
                  >
                    <FiPhone className="shrink-0 text-[#7253F5]" />
                    {phone}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Social */}
        <div className="mt-16">
          <h3 className="text-sm font-semibold tracking-widest uppercase text-[#636363] mb-4">
            Follow us
          </h3>
          <div className="flex gap-4">
            {SOCIAL_LINKS.map(social => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noreferrer"
                aria-label={social.label}
                className="w-11 h-11 flex items-center justify-center rounded-full border border-gray-200 text-[#121212] hover:bg-[#7253F5] hover:text-white hover:border-[#7253F5] transition-colors"
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  )
}
